
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Clock, Mail, Printer, Navigation } from 'lucide-react';
import { DOMAIN, BRAND } from '../constants';
import { SectionHero, Reveal } from '../components/UI';

export const Contact: React.FC = () => {
  const pageTitle = "Contactez-nous | Béton Mobile des Cantons | Granby";
  const pageDesc = "Coordonnées complètes de Béton Mobile des Cantons à Granby. Téléphone, adresse et courriel pour vos projets de béton et plancher radiant.";
  const canonicalUrl = `${DOMAIN}/contact`;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Béton Mobile des Cantons",
    "url": canonicalUrl,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+14503722565",
      "contactType": "customer service",
      "areaServed": "CA",
      "availableLanguage": "French"
    }
  };

  return (
    <div className="bg-slate-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <SectionHero title="Contact" subtitle="Nos Coordonnées" image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop" height="h-[50vh]" />
      
      <div className="container mx-auto px-6 py-20 -mt-24 relative z-20">
        <Reveal>
          <div className="bg-white shadow-2xl border-t-8 border-brand-red overflow-hidden">
            
            {/* Header Section inside Card */}
            <div className="bg-slate-900 text-white p-10 text-center relative">
               <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
               <div className="relative z-10">
                 <h2 className="text-3xl md:text-4xl font-display font-bold uppercase tracking-widest mb-4">Parlons de votre projet</h2>
                 <p className="text-slate-300 font-light max-w-2xl mx-auto text-lg">
                   Notre équipe d'experts est disponible pour répondre à vos questions concernant le béton mobile, le coffrage ou les planchers radiants.
                 </p>
               </div>
            </div>

            {/* Contact Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
              
              {/* Address Card */}
              <div className="p-10 hover:bg-slate-50 transition-colors group flex flex-col items-center text-center">
                 <div className="w-16 h-16 bg-brand-red/10 text-brand-red rounded-full flex items-center justify-center mb-6 group-hover:bg-brand-red group-hover:text-white transition-all duration-300">
                   <MapPin size={32} />
                 </div>
                 <h3 className="text-xl font-display font-bold text-slate-900 uppercase mb-4">Bureau Chef</h3>
                 <p className="text-slate-600 font-mono mb-6 leading-relaxed">
                   875, rue Dufferin<br/>
                   Granby (Québec)<br/>
                   J1H 0T7
                 </p>
                 <a 
                   href="https://www.google.com/maps/search/?api=1&query=875+rue+Dufferin+Granby+QC" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="mt-auto inline-flex items-center gap-2 text-brand-red font-bold uppercase text-xs tracking-widest border-b-2 border-transparent hover:border-brand-red pb-1 transition-all"
                 >
                   <Navigation size={14} /> Itinéraire
                 </a>
              </div>

              {/* Phone Card */}
              <div className="p-10 hover:bg-slate-50 transition-colors group flex flex-col items-center text-center">
                 <div className="w-16 h-16 bg-brand-red/10 text-brand-red rounded-full flex items-center justify-center mb-6 group-hover:bg-brand-red group-hover:text-white transition-all duration-300">
                   <Phone size={32} />
                 </div>
                 <h3 className="text-xl font-display font-bold text-slate-900 uppercase mb-4">Téléphone</h3>
                 <a href="tel:4503722565" className="text-3xl font-display font-bold text-brand-red hover:text-slate-900 transition-colors mb-2">
                   450 372-2565
                 </a>
                 <div className="flex items-center gap-2 text-slate-400 text-sm font-mono mt-2">
                   <Printer size={14} /> <span>Fax : 450 372-2923</span>
                 </div>
                 <p className="mt-6 text-xs text-slate-500 uppercase tracking-wide font-bold">Estimation Gratuite</p>
              </div>

              {/* Email / Hours Card */}
              <div className="p-10 hover:bg-slate-50 transition-colors group flex flex-col items-center text-center">
                 <div className="w-16 h-16 bg-brand-red/10 text-brand-red rounded-full flex items-center justify-center mb-6 group-hover:bg-brand-red group-hover:text-white transition-all duration-300">
                   <Mail size={32} />
                 </div>
                 <h3 className="text-xl font-display font-bold text-slate-900 uppercase mb-4">Courriel & Horaire</h3>
                 <a href="mailto:info@betonmobiledescantons.com" className="text-lg font-bold text-slate-700 hover:text-brand-red transition-colors mb-6 break-all">
                   info@betonmobiledescantons.com
                 </a>
                 <div className="flex items-center gap-2 text-slate-600 font-mono text-sm bg-slate-100 px-4 py-2 rounded-full">
                   <Clock size={16} className="text-brand-red" /> 
                   <span>Lun - Ven: 7h00 - 17h00</span>
                 </div>
              </div>

            </div>
          </div>
        </Reveal>
        
        <div className="mt-12 text-center">
            <h3 className="text-slate-400 font-display font-bold uppercase text-lg mb-4">Zones desservies</h3>
            <div className="flex flex-wrap justify-center gap-3">
                {["Granby", "Haute-Yamaska", "Cantons-de-l'Est", "Estrie", "Montérégie"].map((zone, i) => (
                    <span key={i} className="px-4 py-2 bg-white border border-slate-200 text-slate-600 font-mono text-xs uppercase tracking-wider shadow-sm">
                        {zone}
                    </span>
                ))}
            </div>
        </div>

      </div>
    </div>
  );
};
